import { SubscriptionLimitModal } from "@/components/modals/subscription-limit-modal";
import { Toaster } from "@/components/ui/sonner";
import { useWorkspaceInit } from "@/hooks/use-workspace-init";
import { useWorkspaceSocket } from "@/hooks/use-workspace-socket";
import { useSubscriptionModalStore } from "@/store/subscription-modal-store";
import { queryClient } from "@/lib/query-client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ClerkProvider } from "@clerk/clerk-react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router";
import "./index.css";
import { routes } from "./routes/routes";

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Clerk Publishable Key");
}

const root = document.getElementById("root");
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "PLACEHOLDER_CLIENT_ID";

import { PostHogProvider, PostHogPageView } from "@/providers/posthog-provider";
import { SocketProvider } from "@/providers/socket-provider";

// Inner component that lives INSIDE SocketProvider so hooks can access socket context
function AppContent() {
  useWorkspaceSocket();
  const { isOpen, closeModal } = useSubscriptionModalStore();

  return (
    <BrowserRouter>
      <PostHogPageView />
      {routes}
      <SubscriptionLimitModal open={isOpen} onOpenChange={closeModal} />
    </BrowserRouter>
  );
}

function App() {
  useWorkspaceInit();

  return (
    <StrictMode>
      <PostHogProvider>
        <QueryClientProvider client={queryClient}>
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <ClerkProvider
              publishableKey={PUBLISHABLE_KEY}
              afterSignOutUrl="/"
              allowedRedirectOrigins={[
                "https://app.startupfuel.com",
                "https://staging.startupfuel.com",
                "https://dev.startupfuel.com",
              ]}
            >
              <SocketProvider>
                <AppContent />
              </SocketProvider>
            </ClerkProvider>
          </GoogleOAuthProvider>
          <Toaster />
          <ReactQueryDevtools />
        </QueryClientProvider>
      </PostHogProvider>
    </StrictMode>
  );
}

ReactDOM.createRoot(root as HTMLElement).render(<App />);
